import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getAppData, getAppInput} from "../../store/slices/appSlice";
import {useTranslation} from "react-i18next";
import {
    getSelectedCustomer,
    initializeStorage,
    saveProductSelectionUserConfiguration,
    setSelectedCustomer,
    setSelectedProduct, setSelectedVehicleID
} from "../../store/slices/userConfigurationsSlice";
import CONST from "../../config/CONST";
import {
    getFinancialProductConfigurationByProductSelected,
    getFinancialTypeProductEIM,
} from "../../helpers/financialProductConfigurationHelpers";
import CustomersSelection from "./CustomersSelection/CustomersSelection";
import FinancialProductsSelection from "./FinancialProductsSelection/FinancialProductsSelection";
import {getFinancialConfiguration, setFinancialConfiguration} from "../../store/slices/financialConfigurationsSlice";
import GATracker, {GAEventBuildObject} from "../../helpers/GATracker";
import {cashPriceProxy} from "../../classes/cashPriceProxy";
import {buildFinancialProductConfiguration} from "../../helpers/financialInputHelper/financialConfigurationInputHelper";

const UserConfiguration = () => {

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const appData = useSelector(getAppData);
    const appInput = useSelector(getAppInput);
    const selectedCustomer = useSelector(getSelectedCustomer) || appData.touchpointConfig.defaults.customer;
    const selectedVehicleType = appData.input.vehicleType || appData.touchpointConfig.defaults.vehicleCondition;
    let customersAvailable = appData.touchpointConfig.defaults.availableCustomer;
    // financial product configuration for selected product
    const financialProductConfiguration = useSelector(getFinancialConfiguration);

    useEffect(() => {
        // do nothing if any property in appData is null
        for (const property in appData) {
            if(appData[property] === null) {
                return;
            }
        }
    }, [appData]);

    useEffect(() => {
        setupFinancialProduct(null);
        // eslint-disable-next-line
    }, [selectedCustomer]);

    useEffect(() => {
        let userConfigurationForCurrentEIM = null;
        if(appData && appInput.EIM) {
            setupFinancialProduct(userConfigurationForCurrentEIM);
        }
        // eslint-disable-next-line
    }, [appData, appInput, selectedCustomer] );



    // handle analytics events
    const [customerSelectionHasBeenClicked, setCustomerSelectionHasBeenClicked] = useState(false);
    const [productSelectionHasBeenClicked, setProductSelectionHasBeenClicked] = useState(false);
    useEffect(() => {

        // customer selection
        if(customerSelectionHasBeenClicked === true && appData && financialProductConfiguration) {
            setCustomerSelectionHasBeenClicked(false);
            GATracker.trackEvent(GAEventBuildObject({
                event: GATracker.EVENTS.CUSTOMER_SELECTION,
                appData: appData,
                financialConfiguration: financialProductConfiguration
            }));
        }

        // product selection
        if(productSelectionHasBeenClicked === true && appData && financialProductConfiguration) {
            setProductSelectionHasBeenClicked(false);
            GATracker.trackEvent(GAEventBuildObject({
                event: GATracker.EVENTS.PRODUCT_SELECTION,
                appData: appData,
                financialConfiguration: financialProductConfiguration
            }));
        }

    // eslint-disable-next-line
    }, [financialProductConfiguration]);


    const setupFinancialProduct = (userConfigurationForCurrentEIM) => {
        const browserStorage = window[CONST.BROWSER_STORAGE_TYPE];
        if(browserStorage) {
            const rawData = browserStorage.getItem(CONST.BROWSER_STORAGE_KEY);
            const data = JSON.parse(rawData);
            //if(!userConfigurationForCurrentEIM){
            dispatch(setSelectedVehicleID(appData.input.EIM));
            Object.keys(customersAvailable).forEach(customer => {
                dispatch(initializeStorage({
                    EIM: appData.input.EIM,
                    vehicleType: selectedVehicleType,
                    customer: customer,
                    fieldValue: getFinancialTypeProductEIM(appData.modelsRatesMapping.find(c => c.EIM === appData.input.EIM && c.vehicleType === selectedVehicleType), selectedVehicleType, customersAvailable[customer], customer).financialProducts,
                }));
            });

            if (data && data.userConfigurations && data.userConfigurations.configurations && data.userConfigurations.configurations.length > 0) {
                userConfigurationForCurrentEIM = data.userConfigurations.configurations.find(c => c.EIM === appData.input.EIM && c.selectedProduct && c.vehicleType === selectedVehicleType && c.customer === selectedCustomer);
            }
        }

        const _rateID = getFinancialTypeProductEIM(appData.modelsRatesMapping.find(c => c.EIM === appData.input.EIM), selectedVehicleType, customersAvailable[selectedCustomer], selectedCustomer).financialProducts.find(c => c.defaultFinancialProduct).rateID;

        const _financialProductConfiguration = getFinancialProductConfiguration({
            EIM: appData.input.EIM,
            UIC: appData.modelsRatesMapping.find(c => c.EIM === appData.input.EIM && c.vehicleType === selectedVehicleType).UIC_code,
            vehicleType: userConfigurationForCurrentEIM ? userConfigurationForCurrentEIM.vehicleType : appData.touchpointConfig.defaults.vehicleCondition,
            productTypeCode: userConfigurationForCurrentEIM ? userConfigurationForCurrentEIM.productTypeCode : appData.modelsRatesMapping.find(c => c.EIM === appData.input.EIM && c.vehicleType === selectedVehicleType)[selectedCustomer +`_defaultRentalProduct`],
            rateID: userConfigurationForCurrentEIM ? userConfigurationForCurrentEIM.rateID : _rateID,
            customer: userConfigurationForCurrentEIM ? userConfigurationForCurrentEIM.customer : appData.touchpointConfig.defaults.customer
        });

        const dynamicCashPriceObject = new cashPriceProxy('cashPriceObject' + _financialProductConfiguration.productTypeCode);

        if(appData.touchpointConfig[_financialProductConfiguration.selectedCustomer][_financialProductConfiguration.productTypeCode] &&
            appData.touchpointConfig[_financialProductConfiguration.selectedCustomer][_financialProductConfiguration.productTypeCode].setVat) {
            _financialProductConfiguration.nissanVehiclePriceNoVAT = dynamicCashPriceObject.getCashPrice ({
                vehiclePrice: appData.input.price,
                setVat: appData.touchpointConfig[_financialProductConfiguration.selectedCustomer][_financialProductConfiguration.productTypeCode] ? appData.touchpointConfig[_financialProductConfiguration.selectedCustomer][_financialProductConfiguration.productTypeCode].setVat : false,
                vatValue: appData.touchpointConfig.defaults.vatValue,
                BPM: _financialProductConfiguration.BPM ? _financialProductConfiguration.BPM : null
            });
        }
        //save FinancialConfiguration in component state
        dispatch(setFinancialConfiguration(buildFinancialProductConfiguration(_financialProductConfiguration, appData.touchpointConfig[_financialProductConfiguration.selectedCustomer][_financialProductConfiguration.productTypeCode].setVat)));


    }

    const getFinancialProductConfiguration = ( {EIM, UIC, vehicleType, productTypeCode, rateID, customer} ) => {
        // get financial product configuration
        const configurationRates = getFinancialProductConfigurationByProductSelected(appData, customer, productTypeCode, rateID);
        const configuration = {...configurationRates, UIC};
        const browserStorage = window[CONST.BROWSER_STORAGE_TYPE];
        if(browserStorage) {
            const rawData = browserStorage.getItem(CONST.BROWSER_STORAGE_KEY);
            const data = JSON.parse(rawData);
            if(data && data.userConfigurations && data.userConfigurations.configurations && data.userConfigurations.configurations.length > 0) {
                const userConfigurationForCurrentEIM = data.userConfigurations.configurations.find(c => c.EIM === EIM && c.selectedProduct && c.vehicleType === vehicleType && c.customer === customer);
                if(userConfigurationForCurrentEIM) {
                    configuration.productTypeCode = productTypeCode ? productTypeCode : userConfigurationForCurrentEIM.productTypeCode || null;
                    configuration.selectedCustomer = customer ? customer : userConfigurationForCurrentEIM.customer || null;
                    configuration.vehicleType = vehicleType ? vehicleType : userConfigurationForCurrentEIM.vehicleType || null;
                }
                else {
                    configuration.productTypeCode = productTypeCode ? productTypeCode : null;
                    configuration.selectedCustomer = customer ? customer : null;
                    configuration.vehicleType = vehicleType ? vehicleType : null;
                }
            }
        }
        return configuration;
    }

    const onCustomerChange = (value) => {
        //dispatch(setSelectedCustomer(value));
        dispatch(setSelectedCustomer({
            EIM: appData.input.EIM,
            customer: value,
            vehicleType: selectedVehicleType,
            appData: appData,
            rates: appData.rates
        }));

        setCustomerSelectionHasBeenClicked(true);
        setupFinancialProduct(null);

    }

    const onFinancialProductChange = (value) => {
        dispatch(saveProductSelectionUserConfiguration({
            EIM: appData.input.EIM,
            fieldValue: value,
            vehicleType: selectedVehicleType,
            appData: appData,
            rates: appData.rates,
            // GATrackerEvent: GATracker.EVENTS.PRODUCT_SELECTION
        }));
        setupFinancialProduct(null);
        dispatch(setSelectedProduct({value: value, EIM: appData.input.EIM}));

        setProductSelectionHasBeenClicked(true);
    }


    if(!appData || !appData.input || !financialProductConfiguration) {
        return null;
    }

    return (
    <div className={`${CONST.CSS_NAMESPACE}UserConfiguration`}>
        <div className={`${CONST.CSS_NAMESPACE}col-md-12 ${CONST.CSS_NAMESPACE}no-padding`}>
            <div className={`${CONST.CSS_NAMESPACE}UserConfigurationCustomer`}>
                <h2>{t(`SelectCustomer`)}</h2>
                <CustomersSelection
                    financialProductConfiguration={financialProductConfiguration}
                    onChange={onCustomerChange}
                />
            </div>

            { getFinancialTypeProductEIM(appData.modelsRatesMapping.find(c => c.EIM === appData.input.EIM && c.vehicleType === selectedVehicleType), selectedVehicleType, customersAvailable[selectedCustomer], selectedCustomer).financialProducts.length >= 1 &&
                <div className={`${CONST.CSS_NAMESPACE}UserConfigurationFinancialProducts`}>
                    <h2>{t('SelectFinancialProducts')}</h2>
                    <FinancialProductsSelection
                        financialProductConfiguration={financialProductConfiguration}
                        onChange={onFinancialProductChange}
                    />
                </div>
            }
        </div>

    </div>

    )

}

export default UserConfiguration;